/**
 * 标准配合求解器类型。
 *
 * 文件职责：
 * 1. 定义独立求解器的 plain data 输入；
 * 2. 定义求解诊断与输出 transform；
 * 3. 让业务层只依赖稳定类型，不耦合求解器内部实现。
 */

import type { AssemblyComponentFixationState } from '../component-fixation';
import type { ComponentTransformSnapshot } from '../component-instance';
import type { AssemblyMateDefinition, MateSolveStatus } from '../definition';

/**
 * 距离配合在求解时使用的符号分支策略。
 */
export enum StandardMateDistanceBranchPolicy {
  /**
   * 使用 Mate 定义的选择顺序与反转尺寸决定距离方向。
   */
  SelectionOrder = 'selectionOrder',
  /**
   * 在可行的两个距离方向中选择最接近当前移动件姿态的结果。
   *
   * 说明：平面-轴线类距离不使用最近位姿切侧，而是保持 Mate 创建时的选择侧，
   * 只有 `Flip Dimension` 才能切到另一侧，以对齐 SOLIDWORKS 拖动行为。
   */
  Continuous = 'continuous',
}

/**
 * 组件世界包围盒快照。
 *
 * 设计说明：
 * 求解器仍然只消费 plain data。运行时若需要在 Mate 预览阶段做初始姿态避让，
 * 只能把 Object3D 包围盒转换成该结构传入，避免求解器依赖 Viewer 或 three.js 场景对象。
 */
export interface AssemblyMateSolverWorldBounds {
  /** 世界坐标最小点。 */
  readonly min: readonly [number, number, number];
  /** 世界坐标最大点。 */
  readonly max: readonly [number, number, number];
}

/**
 * 求解器组件状态。
 */
export interface AssemblyMateSolverComponentState {
  /** 组件实例 ID。 */
  readonly componentInstanceId: string;
  /** 组件当前 local transform。 */
  readonly transform: ComponentTransformSnapshot;
  /**
   * 组件当前 world transform。
   *
   * 设计说明：
   * MateEntityRef.geometry 是选择瞬间的 world-space BRep 描述符。子装配或父级节点有位姿时，
   * 仅靠 local transform 无法把该描述符还原到当前 world space；运行时应尽量提供该字段，
   * 纯数据测试和顶层装配可继续省略并回退到 local transform。
   */
  readonly worldTransform?: ComponentTransformSnapshot;
  /**
   * 组件当前世界包围盒。
   *
   * 说明：
   * 该字段只用于 Mate PropertyManager 新建预览的初始姿态避让，不参与已提交 Mate 的拖拽热路径。
   */
  readonly worldBounds?: AssemblyMateSolverWorldBounds;
  /** 组件固定/浮动状态。 */
  readonly fixationState: AssemblyComponentFixationState | 'fixed' | 'floating';
}

/**
 * 用户直接驱动组件时需要保留的位姿分量。
 *
 * 设计说明：
 * Move/Rotate Component 不是把零件临时改成 fixed。移动命令通常保留用户给出的平移，
 * 旋转命令通常保留用户给出的旋转，但闭环机构可能需要释放另一类分量才能在剩余自由度内求解。
 */
export interface StandardMateDrivenComponentConstraint {
  /** 是否保留 driven 组件当前平移分量。缺省时保持旧行为，即保留。 */
  readonly preserveTranslation?: boolean;
  /** 是否保留 driven 组件当前旋转分量。缺省时保持旧行为，即保留。 */
  readonly preserveRotation?: boolean;
}

/**
 * 单条配合应用结果。
 *
 * 设计说明：
 * 该类型只描述求解器内部某条 Mate 对组件 transform 的影响，业务层仍消费
 * `AssemblyMateSolveDiagnostic` 与最终 `StandardMateSolverResult`。
 */
export interface StandardMateApplyResult {
  /** 求解状态。 */
  readonly status: MateSolveStatus;
  /** 诊断信息。 */
  readonly message?: string;
  /** 移动组件 ID。 */
  readonly movedComponentInstanceId?: string;
  /** 移动组件求解后 transform。 */
  readonly transform?: ComponentTransformSnapshot;
}

/**
 * 单条 Mate 的固定侧 / 移动侧解析结果。
 */
export interface StandardMateMovingSideResolution {
  /** 目标实体。 */
  readonly fixedEntity: AssemblyMateDefinition['firstEntity'];
  /** 移动实体。 */
  readonly movingEntity: AssemblyMateDefinition['secondEntity'];
  /** 目标组件。 */
  readonly fixedComponent: AssemblyMateSolverComponentState;
  /** 移动组件。 */
  readonly movingComponent: AssemblyMateSolverComponentState;
  /** 是否移动第一选择。 */
  readonly movingFirst: boolean;
}

/**
 * 标准配合求解器调试追踪选项。
 *
 * 设计说明：
 * 该配置只用于人工复现复杂装配求解问题。默认关闭，开启后输出结构化 plain data，
 * 不能依赖 Viewer、UI 或 Object3D，也不能改变任何求解分支。
 */
export interface StandardMateSolverDebugTraceOptions {
  /** 是否开启追踪；对象形式缺省视为开启。 */
  readonly enabled?: boolean;
  /** 本次追踪标签，便于在浏览器控制台筛选。 */
  readonly label?: string;
  /** 是否输出已成功求解的结果；缺省只输出失败或未解结果，降低拖拽热路径日志量。 */
  readonly includeSolved?: boolean;
  /**
   * 自定义追踪接收器。
   *
   * 说明：
   * 该回调只接收可序列化快照。测试或纯 cad-core 调用可注入它收集日志；
   * 运行时手动排查通常使用全局开关和控制台输出。
   */
  readonly emit?: (snapshot: unknown) => void;
}

/**
 * 标准配合求解输入。
 */
export interface StandardMateSolverInput {
  /** 参与求解的组件状态。 */
  readonly components: Readonly<Record<string, AssemblyMateSolverComponentState>>;
  /** 参与求解的配合定义。 */
  readonly mateDefinitions: readonly AssemblyMateDefinition[];
  /**
   * 交互中由用户直接拖动或旋转的组件实例 ID。
   *
   * 说明：
   * - 它不是持久 fixed 状态，只在本次交互求解中描述用户直接操作的组件；
   * - 若另一侧组件真实 fixed，则真实 fixed 优先，驱动侧会被修正回满足配合的位置；
   * - 全浮动连通图会把驱动侧作为交互根，约束沿 Mate 图传播到其它 floating 组件；
   * - 这样可以对齐 SOLIDWORKS 拖动浮动组件时当前组件可移动、其它浮动组件按 Mate 自由度跟随的行为。
   */
  readonly drivenComponentInstanceIds?: readonly string[];
  /**
   * driven 组件在本次交互中需要保留的位姿分量。
   *
   * 说明：
   * 未提供时保持历史语义，driven 组件的完整 transform 都被视为用户输入并保持不变。
   * RotateService 会显式声明“保留旋转、释放平移”，让四连杆等闭环机构能在用户旋转意图下联动求解。
   */
  readonly drivenComponentConstraints?: Readonly<Record<string, StandardMateDrivenComponentConstraint>>;
  /**
   * PropertyManager 预览阶段希望保持为基准的组件实例 ID。
   *
   * 说明：
   * - SOLIDWORKS 新建 Mate 预览以第一选择实体作为交互基准，切换对齐时应翻转第二选择侧；
   * - 它不是固定状态，也不代表用户正在拖动组件；真实 fixed 与直接拖动侧仍由求解计划优先处理；
   * - 该字段只描述命令预览的移动侧偏好，避免多 Mate 子图 fallback 按组件 ID 错选锚点。
   */
  readonly previewAnchorComponentInstanceIds?: readonly string[];
  /**
   * 距离配合符号分支策略。
   *
   * 设计说明：
   * - 新建/预览 Mate 时沿用选择顺序，保证“反转尺寸”能明确翻到另一侧；
   * - 已提交 Mate 在拖动后重求解时应选择连续分支，避免跟随组件穿到另一侧造成闪现。
   */
  readonly distanceBranchPolicy?: StandardMateDistanceBranchPolicy;
  /**
   * 是否允许临时 Mate 使用初始姿态增强。
   *
   * 设计说明：
   * - PropertyManager 首次预览需要自动贴合代表点或包围盒，避免两个零件离得过远或互相包含；
   * - 用户在预览约束下继续拖动、以及点击确认时，当前位姿已经是用户认可的位置，
   *   必须关闭该增强以保留剩余自由度上的手动放置结果。
   */
  readonly useTransientInitialPlacement?: boolean;
  /**
   * 线性尺寸换算比例。
   *
   * 说明：
   * - Distance 等 Mate PropertyManager 数值保持 SOLIDWORKS 风格毫米语义；
   * - 求解器内部几何描述符是 world/render space，因此进入求解前必须乘以该比例；
   * - 缺省使用 imported-model 模块定义的毫米到渲染空间比例。
  */
  readonly linearDimensionScale?: number;
  /** 可选：只求解指定配合。 */
  readonly targetMateId?: string;
  /**
   * 本轮需要主动执行求解的 Mate ID。
   *
   * 说明：
   * - residual-first 只能判断“当前残差是否满足”，不能表达 PropertyManager 中用户主动切换的分支；
   * - 例如圆柱同轴心的最终残差按无向中心线成立，但用户切换 Aligned / AntiAligned 时仍需要翻转姿态；
   * - 该字段只描述求解意图，不改变 Mate Definition，也不依赖 UI 或 Viewer。
   */
  readonly activeMateIds?: readonly string[];
  /**
   * 尚未提交到装配体文档的临时 Mate ID。
   *
   * 说明：
   * - PropertyManager 预览会把临时 Mate 放入已提交 Mate 的上下文中一起校验；
   * - 临时 Mate 自身残差失败只代表当前草稿未解，不应仅因上下文里存在旧 Mate 就被升级为过定义；
   * - 已提交 Mate 的残差、重复自由度以及真实冲突仍由求解器按 overDefined 输出。
   */
  readonly transientMateIds?: readonly string[];
  /** 数值容差。 */
  readonly epsilon?: number;
  /** 角度容差。 */
  readonly angleEpsilon?: number;
  /**
   * 可选调试追踪。
   *
   * 说明：
   * - 该字段不会参与求解决策，只在求解完成后输出结构化诊断；
   * - 浏览器现场排查也可以通过全局变量 `__PUPPYCAD_STANDARD_MATE_SOLVER_TRACE__` 开启。
   */
  readonly debugTrace?: boolean | StandardMateSolverDebugTraceOptions;
  /**
   * 是否启用全局数值求解内核候选。
   *
   * 说明：
   * - 内核只在"逐条投影与解析闭合规则全部失败"的路径上作为最后兜底候选运行，产物经整轮
   *   validation 通过才会被采纳，缺省开启；
   * - 显式传 false 可关闭；未传时也可用全局键
   *   `__PUPPYCAD_STANDARD_MATE_GLOBAL_NUMERIC_KERNEL__ = false` 整体关闭（回退开关）。
   */
  readonly enableGlobalNumericKernel?: boolean;
}

/**
 * 单条配合求解诊断。
 */
export interface AssemblyMateSolveDiagnostic {
  /** 配合 ID。 */
  readonly mateId: string;
  /** 求解状态。 */
  readonly status: MateSolveStatus;
  /** 诊断信息。 */
  readonly message?: string;
  /** 被移动的组件实例 ID。 */
  readonly movedComponentInstanceId?: string;
}

/**
 * 标准配合求解结果。
 */
export interface StandardMateSolverResult {
  /** 总体状态。 */
  readonly status: MateSolveStatus;
  /** 求解后的组件 transform。 */
  readonly componentTransforms: Readonly<Record<string, ComponentTransformSnapshot>>;
  /** 单条配合诊断。 */
  readonly diagnostics: readonly AssemblyMateSolveDiagnostic[];
}
